<template>
    <div class="min-h-screen bg-gray-50">
        <!-- Hero Section -->
        <section class="relative overflow-hidden" style="background: linear-gradient(to bottom right, #A3E4DB, #C4E49F);">
            <div class="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>

            <!-- Hero Content -->
            <div class="relative container mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-16">
                <div class="text-center text-gray-800">
                    <h1 class="text-3xl sm:text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                        常見<span style="color: #F4D35E;">問題</span>
                    </h1>
                    <p class="text-lg sm:text-xl lg:text-2xl mb-8 text-gray-700 leading-relaxed max-w-3xl mx-auto">
                        快速找到您需要的答案
                    </p>
                </div>
            </div>

            <!-- Decorative Elements -->
            <div class="absolute top-0 right-0 w-1/3 h-full opacity-10">
                <div class="w-full h-full bg-gradient-to-l from-white to-transparent"></div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-6">
                <div class="max-w-4xl mx-auto space-y-4">
                    <div v-for="(faq, index) in faqs" :key="index"
                        class="bg-white rounded-xl shadow-md overflow-hidden">
                        <button @click="toggleFaq(index)"
                            class="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200">
                            <span class="font-medium text-gray-800">{{ faq.question }}</span>
                            <svg :class="{ 'rotate-180': faq.isOpen }"
                                class="w-5 h-5 text-gray-500 transition-transform duration-300" fill="none"
                                stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <!-- 加上 Vue 過渡動畫 -->
                        <transition name="faq-slide">
                            <div v-show="faq.isOpen" class="px-6 pb-4">
                                <p class="text-gray-600 leading-relaxed">{{ faq.answer }}</p>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </section>


        <!-- 關注我們區塊 -->
        <section class="py-16 bg-white">
            <div class="container mx-auto px-6 text-center">
                <!-- 標題放大 -->
                <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                    <span style="color: #F4D35E;">關注</span>我們
                </h2>

                <!-- 說明文字放大 -->
                <p class="text-lg sm:text-xl text-gray-600 mb-10">若有其他問題，歡迎聯絡我們</p>

                <!-- Icon 加大與間距優化 -->
                <div class="flex flex-wrap justify-center gap-10 text-gray-500 text-6xl sm:text-7xl">
                    <a href="https://www.facebook.com" target="_blank"
                        class="hover:text-blue-600 transition-transform transform hover:scale-110">
                        <i class="fab fa-facebook-f"></i>
                    </a>
                    <a href="https://www.instagram.com" target="_blank"
                        class="hover:text-pink-500 transition-transform transform hover:scale-110">
                        <i class="fab fa-instagram"></i>
                    </a>
                    <a href="https://line.me" target="_blank"
                        class="hover:text-green-500 transition-transform transform hover:scale-110">
                        <i class="fab fa-line"></i>
                    </a>
                    <a href="https://www.youtube.com" target="_blank"
                        class="hover:text-red-600 transition-transform transform hover:scale-110">
                        <i class="fab fa-youtube"></i>
                    </a>
                </div>
            </div>
        </section>

    </div>
</template>

<script setup>

import { ref } from 'vue'
// FAQ 資料
const faqs = ref([
    {
        question: '智光系統支援哪些作業系統？',
        answer: '智光系統支援 Windows、macOS 和 Linux 等主流作業系統，同時提供雲端版本，可透過瀏覽器直接使用，無需安裝任何軟體。',
        isOpen: false
    },
    {
        question: '系統導入需要多長時間？',
        answer: '系統導入時間依企業規模和需求而定，一般中小企業約需 2-4 週，大型企業可能需要 1-3 個月。我們會提供詳細的導入計劃和時程表。',
        isOpen: false
    },
    {
        question: '是否提供教育訓練服務？',
        answer: '是的，我們提供完整的教育訓練服務，包括現場教學、線上課程、操作手冊和影片教學。智光i學園也提供持續的學習資源。',
        isOpen: false
    },
    {
        question: '系統的資料安全性如何保障？',
        answer: '我們採用銀行級別的加密技術，定期備份資料，並提供多重身份驗證。所有資料均儲存在符合國際標準的資料中心。',
        isOpen: false
    },
    {
        question: '可以客製化開發功能嗎？',
        answer: '可以的，我們提供客製化開發服務，可根據您的特殊需求開發專屬功能模組，確保系統完全符合您的業務流程。',
        isOpen: false
    },
    {
        question: '系統維護和技術支援如何處理？',
        answer: '我們提供 24/7 技術支援服務，包括電話、郵件和遠端協助。定期系統更新和維護都包含在服務合約中。',
        isOpen: false
    }
])

// 切換 FAQ 顯示狀態
const toggleFaq = (index) => {
    faqs.value[index].isOpen = !faqs.value[index].isOpen
}
</script>

<style lang="scss" scoped>
// 修正 z-index 層級問題
.text-gray-800 {
    position: relative;
    z-index: 1;
}

// Hero section 的背景層級
.absolute.inset-0 {
    z-index: 0;
}

// 裝飾元素層級
.absolute.top-0.right-0 {
    z-index: 0;
}

section.bg-white {
    position: relative;
    z-index: 1;
}

// Hero 區域動畫
.hero-content {
    animation: fadeInUp 1s ease-out;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// FAQ 展開動畫
.faq-item {
    transition: all 0.3s ease;

    .faq-content {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease, padding 0.3s ease;
    }

    &.open .faq-content {
        max-height: 200px;
        padding-bottom: 1rem;
    }
}

/* FAQ 平滑展開動畫 */
.faq-slide-enter-active,
.faq-slide-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.faq-slide-enter-from,
.faq-slide-leave-to {
    max-height: 0;
    opacity: 0;
    padding-bottom: 0;
}

.faq-slide-enter-to,
.faq-slide-leave-from {
    max-height: 500px;
    /* 根據內容長度調整 */
    opacity: 1;
    padding-bottom: 1rem;
}
</style>